import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, X } from 'lucide-react';
import { contentApi } from '../../services/contentApi';
import { CatalogTreeCategory, CatalogTreeCourse, CatalogTreeLab, CatalogTreeModule, CatalogTreeTopic } from '../../types/content';
import Button from '../UI/Button';

type Mode = 'module' | 'lab';

interface Props {
  open: boolean;
  mode: Mode;
  onClose: () => void;
  onSelectModule?: (moduleId: number) => void;
  onSelectLab?: (lab: CatalogTreeLab, topic: CatalogTreeTopic) => void;
}

const CatalogBrowseModal: React.FC<Props> = ({ open, mode, onClose, onSelectModule, onSelectLab }) => {
  const [categories, setCategories] = useState<CatalogTreeCategory[]>([]);
  const [unlinked, setUnlinked] = useState<CatalogTreeLab[]>([]);
  const [openCats, setOpenCats] = useState<number[]>([]);
  const [openCourses, setOpenCourses] = useState<number[]>([]);
  const [openModules, setOpenModules] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    contentApi.catalogTree().then((r) => {
      setCategories(r.data.categories || []);
      setUnlinked(r.data.unlinkedLabs || []);
    }).catch(() => setError('Unable to load the catalog.'));
  }, [open]);

  if (!open) return null;

  const toggle = (list: number[], id: number, set: (next: number[]) => void) => {
    set(list.includes(id) ? list.filter((item) => item !== id) : [...list, id]);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto"
      onMouseDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
    >
      <div
        className="w-full max-w-2xl rounded-2xl bg-white shadow-xl my-8"
        role="dialog"
        aria-modal="true"
        onMouseDown={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h2 className="font-semibold text-gray-900">
            {mode === 'module' ? 'Browse modules' : 'Browse labs'}
          </h2>
          <button type="button" aria-label="Close" onClick={onClose} className="text-gray-500 hover:text-gray-800">
            <X className="h-5 w-5" />
          </button>
        </div>
        <p className="px-5 pt-3 text-sm text-gray-600">
          Category → Course → Module{mode === 'lab' ? ' → Topic → Lab' : ''}. Select an item to link it.
        </p>
        {error && <p className="mx-5 mt-3 text-sm text-error-700">{error}</p>}
        <div className="p-4 max-h-[70vh] overflow-y-auto space-y-2">
          {categories.map((category) => (
            <div key={`${category.id}-${category.name}`} className="rounded-xl border border-gray-200">
              <button
                type="button"
                className="w-full flex items-center gap-2 px-3 py-2 text-left font-semibold text-gray-900"
                onClick={() => toggle(openCats, category.id, setOpenCats)}
              >
                {openCats.includes(category.id) ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                {category.name}
              </button>
              {openCats.includes(category.id) && (
                <div className="px-3 pb-3 space-y-1">
                  {category.courses.map((course) => (
                    <CourseNode
                      key={course.id}
                      course={course}
                      mode={mode}
                      open={openCourses.includes(course.id)}
                      openModules={openModules}
                      onToggleCourse={() => toggle(openCourses, course.id, setOpenCourses)}
                      onToggleModule={(id) => toggle(openModules, id, setOpenModules)}
                      onSelectModule={onSelectModule}
                      onSelectLab={onSelectLab}
                    />
                  ))}
                </div>
              )}
            </div>
          ))}
          {mode === 'lab' && unlinked.length > 0 && (
            <div className="rounded-xl border border-dashed border-gray-300 p-3">
              <p className="text-sm font-medium text-gray-700 mb-2">Labs not yet linked to a topic</p>
              {unlinked.map((lab) => (
                <button
                  key={lab.id}
                  type="button"
                  className="block w-full text-left text-sm px-2 py-1 rounded hover:bg-gray-50"
                  onClick={() => onSelectLab?.(lab, { id: 0, title: '', slug: '', isActive: true, labs: [] })}
                >
                  {lab.name}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-gray-200 flex justify-end">
          <Button type="button" variant="outline" onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
};

const CourseNode: React.FC<{
  course: CatalogTreeCourse;
  mode: Mode;
  open: boolean;
  openModules: number[];
  onToggleCourse: () => void;
  onToggleModule: (id: number) => void;
  onSelectModule?: (moduleId: number) => void;
  onSelectLab?: (lab: CatalogTreeLab, topic: CatalogTreeTopic) => void;
}> = ({ course, mode, open, openModules, onToggleCourse, onToggleModule, onSelectModule, onSelectLab }) => (
  <div className="rounded-lg bg-gray-50">
    <button type="button" className="w-full flex items-center gap-2 px-2 py-1.5 text-sm font-medium" onClick={onToggleCourse}>
      {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
      {course.title}
    </button>
    {open && (
      <ul className="px-3 pb-2 space-y-1">
        {course.modules.map((module) => (
          <li key={module.id}>
            <div className="flex items-center gap-2">
              {mode === 'lab' ? (
                <button type="button" className="text-gray-500" onClick={() => onToggleModule(module.id)}>
                  {openModules.includes(module.id) ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                </button>
              ) : null}
              <span className="text-sm text-gray-800 flex-1">{module.title}</span>
              {mode === 'module' ? (
                <Button type="button" size="sm" variant="ghost" onClick={() => onSelectModule?.(module.id)}>Include</Button>
              ) : null}
            </div>
            {mode === 'lab' && openModules.includes(module.id) && (
              <ul className="ml-6 mt-1 space-y-1">
                {module.topics.map((topic) => (
                  <li key={topic.id}>
                    <p className="text-xs uppercase tracking-wide text-gray-500">{topic.title}</p>
                    {topic.labs.map((lab) => (
                      <button
                        key={lab.id}
                        type="button"
                        className="block w-full text-left text-sm px-2 py-1 rounded hover:bg-white"
                        onClick={() => onSelectLab?.(lab, topic)}
                      >
                        {lab.name}
                      </button>
                    ))}
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    )}
  </div>
);

export default CatalogBrowseModal;
